import { makeStyles, Theme } from '@material-ui/core/styles'
import Switch from '@material-ui/core/Switch'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'

interface StyleProps {
    sliderTrackColor?: string
    sliderThumbColor?: string
    sliderThumbBorder?: string
    isWorkspaceToggle?: boolean
    noMargin?: boolean
}

const useToggleSwitchStyles = makeStyles((theme: Theme) => ({
    root: {
        width: (props: StyleProps) => (props.isWorkspaceToggle ? 28.8 : 42),
        height: (props: StyleProps) => (props.isWorkspaceToggle ? 18 : 26),
        padding: 0,
        margin: (props: StyleProps) => (props.noMargin ? 0 : theme.spacing(1)),
    },
    rootSmall: {
        width: 35,
        height: 20,
        margin: 5,
        padding: 0,
        marginRight: 0,
    },
    focusVisible: {},
    switchBase: {
        padding: 1,
        '&.Mui-checked': {
            transform: (props: StyleProps) =>
                props.isWorkspaceToggle
                    ? 'translateX(12px)'
                    : 'translateX(16px)',
            color: (props: StyleProps) =>
                props.sliderThumbColor ?? theme.palette.common.white,
            '& + .MuiSwitch-track': {
                background: (props: StyleProps) =>
                    props.sliderTrackColor ?? '#185E8E',
                opacity: 1,
                border: 'none',
            },
        },
        '&$focusVisible $thumb': {
            color: '#304959',
            border: '6px solid #fff',
        },
    },
    thumb: {
        width: (props: StyleProps) => (props.isWorkspaceToggle ? 16 : 24),
        height: (props: StyleProps) => (props.isWorkspaceToggle ? 16 : 24),
        backgroundColor: (props: StyleProps) =>
            props.sliderThumbColor ?? theme.palette.common.white,
        border: (props: StyleProps) => props.sliderThumbBorder ?? 'inherit',
    },
    thumbSmall: {
        width: 18,
        height: 18,
        border: (props: StyleProps) => props.sliderThumbBorder ?? 'inherit',
    },
    track: {
        borderRadius: 13,
        border: `1px solid ${theme.palette.grey[400]}`,
        backgroundColor: (props: StyleProps) => {
            return props.sliderTrackColor ?? '#D3D3D3'
        },
        opacity: 1,
        transition: theme.transitions.create(['background-color', 'border']),
        '&:focus': {
            backgroundColor: (props: StyleProps) => {
                return props.sliderTrackColor ?? '#BDBDBD'
            },
        },
    },
    disabled: {
        backgroundColor: '#bdbdbd',
        color: '#bdbdbd',
        borderRadius: 13,
        border: `1px solid ${theme.palette.grey[400]}`,
    },
    checked: {},
}))

export interface ToggleSwitchProps extends StyleProps {
    name?: string
    checked?: boolean
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        state: boolean
    ) => void
    size?: 'small' | 'large'
    disabled?: boolean
    isWorkspaceToggle?: boolean
    noMargin?: boolean
}
const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
    name,
    checked,
    onChange,
    size = 'large',
    disabled,
    isWorkspaceToggle = false,
    sliderTrackColor,
    sliderThumbColor,
    sliderThumbBorder,
    noMargin = false,
    ...rest
}) => {
    const classes = useToggleSwitchStyles({
        sliderThumbColor,
        sliderTrackColor,
        sliderThumbBorder,
        isWorkspaceToggle,
        noMargin,
    })

    return (
        <Switch
            tabIndex={0}
            name={name}
            disableRipple
            className="toggleSwitch"
            classes={{
                root: size === 'large' ? classes.root : classes.rootSmall,
                switchBase: classes.switchBase,
                thumb: size === 'large' ? classes.thumb : classes.thumbSmall,
                track: !disabled ? classes.track : classes.disabled,
                checked: classes.checked,
            }}
            checked={checked}
            data-testid={`${name}ToggleSwitch`}
            onChange={onChange}
            onKeyDown={onEnterKeyDown(onChange)}
            disabled={disabled}
            inputProps={{ 'aria-label': 'toggle switch', tabIndex: 0 }}
            {...rest}
        />
    )
}

export default ToggleSwitch
